import Axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { env } from '@/config/env';
import { useStore } from '@/stores';
import { router } from '@/routes/router';

const DEFAULT_TIMEOUT = 60000;
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 menit tidak ada aktivitas API → logout

let idleTimer: ReturnType<typeof setTimeout> | null = null;

function resetIdleTimer() {
  if (!useStore.getState().accessToken) return;
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    useStore.setState({ accessToken: null, expiresAt: null });
    router.navigate({ to: '/auth/login' });
  }, IDLE_TIMEOUT_MS);
}

export interface IError {
  errors: {
    detail: string;
    id: string;
    title: string;
  }[];
}

export class AxiosManager {
  public readonly http: AxiosInstance;
  public readonly httpWithAuth: AxiosInstance;

  constructor() {
    this.http = Axios.create({
      baseURL: env.baseUrl,
      timeout: DEFAULT_TIMEOUT,
      headers: {
        'X-Admin': 'admin',
      },
    });

    this.httpWithAuth = Axios.create({
      baseURL: env.baseUrl,
      timeout: DEFAULT_TIMEOUT,
    });

    this.http.interceptors.response.use(
      this.fulfilledResponseInterceptor,
      this.unauthenticatedRejectResponseInterceptor,
    );

    this.httpWithAuth.interceptors.request.use(this.authRequestInterceptor);
    this.httpWithAuth.interceptors.response.use(
      this.fulfilledResponseInterceptor,
      this.rejectResponseInterceptor,
    );
  }

  private async authRequestInterceptor(axiosConfig: InternalAxiosRequestConfig) {
    const token = useStore.getState().accessToken;

    if (axiosConfig.headers) {
      if (token) axiosConfig.headers.authorization = `Bearer ${token}`;
      axiosConfig.headers.Accept = 'application/json';
      axiosConfig.headers['X-Admin'] = 'admin';
    }

    resetIdleTimer();
    return axiosConfig;
  }

  private async fulfilledResponseInterceptor(response: AxiosResponse) {
    resetIdleTimer();
    return response;
  }

  private async unauthenticatedRejectResponseInterceptor(error: AxiosError<IError>) {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      console.warn(
        `Are you sure you use axiosWithToken for ${originalRequest.method?.toUpperCase()} /${
          originalRequest.url
        } ? This is a restricted endpoint.`,
      );
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }

  private async rejectResponseInterceptor(error: AxiosError<IError>) {
    if (error.response?.status === 401) {
      useStore.setState({ accessToken: null, expiresAt: null });
      const currentPath = window.location.pathname + window.location.search;
      router.navigate({ to: '/auth/login', search: { redirect: currentPath } });
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
}
